import { useMemo, useState } from "react";
import { Button } from "Components/Common/Button/Button";
import { InputField } from "Components/Common/InputField/InputField";
import { ApiError } from "Components/Common/ApiError";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import {
  useCreateMailOrderPharmacyDefinition,
  usePreviewMailOrderPharmacyDefinition,
  useUpdateMailOrderPharmacyDefinition,
} from "CustomHooks/useMailOrderPharmacies";

const QUERY_FIELDS = [
  ["name", "Name"],
  ["ncpdpID", "NCPDP ID"],
  ["address", "Address"],
  ["city", "City"],
  ["state", "State"],
  ["zip", "ZIP code"],
  ["phoneOrFax", "Phone or fax"],
  ["specialty0", "Specialty 1"],
  ["specialty1", "Specialty 2"],
];

const queryRowsFromDefinition = (definition) => {
  const rows = (definition?.queries || []).flatMap((query) =>
    Object.entries(query || {})
      .filter(([, value]) => value)
      .map(([field, value]) => ({ field, value })),
  );
  return rows.length ? rows : [{ field: "name", value: "" }];
};

const commaList = (value) =>
  String(value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

const makePayload = (form, queryRows) => ({
  provider: form.provider.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, ""),
  displayName: form.displayName.trim(),
  portalUrl: form.portalUrl.trim(),
  sortOrder: Number(form.sortOrder) || 0,
  queries: queryRows
    .filter((query) => query.value.trim())
    .map((query) => ({ [query.field]: query.value.trim() })),
  matchRules: {
    nameMatchers: commaList(form.nameMatchers),
    ncpdpIds: commaList(form.ncpdpIds),
    address: form.address.trim(),
    city: form.city.trim(),
    state: form.state.trim(),
    zip: form.zip.trim(),
    phone: form.phone.trim(),
  },
});

export const MailOrderPharmacyForm = ({ definition, onClose }) => {
  const [form, setForm] = useState({
    provider: definition?.provider || "",
    displayName: definition?.displayName || "",
    portalUrl: definition?.portalUrl || "",
    sortOrder: definition?.sortOrder ?? 0,
    nameMatchers: (definition?.matchRules?.nameMatchers || []).join(", "),
    ncpdpIds: (definition?.matchRules?.ncpdpIds || []).join(", "),
    address: definition?.matchRules?.address || "",
    city: definition?.matchRules?.city || "",
    state: definition?.matchRules?.state || "",
    zip: definition?.matchRules?.zip || "",
    phone: definition?.matchRules?.phone || "",
  });
  const [queryRows, setQueryRows] = useState(() => queryRowsFromDefinition(definition));
  const [apiError, setApiError] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const preview = usePreviewMailOrderPharmacyDefinition();
  const create = useCreateMailOrderPharmacyDefinition();
  const update = useUpdateMailOrderPharmacyDefinition();
  const saving = create.isLoading || update.isLoading;
  const payload = useMemo(() => makePayload(form, queryRows), [form, queryRows]);
  const canSubmit = payload.provider && payload.displayName && payload.queries.length
    && (payload.matchRules.nameMatchers.length || payload.matchRules.ncpdpIds.length);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setPreviewResult(null);
  };

  const testResolution = () => {
    setApiError("");
    preview.mutate(payload, {
      onSuccess: setPreviewResult,
      onError: (error) => setApiError(errorFormatter(error?.response?.data?.errors || error?.message)),
    });
  };

  const save = () => {
    setApiError("");
    const mutation = definition ? update : create;
    const variables = definition ? { id: definition.id, payload } : payload;
    mutation.mutate(variables, {
      onSuccess: onClose,
      onError: (error) => setApiError(errorFormatter(error?.response?.data?.errors || error?.message)),
    });
  };

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="relative mx-auto my-8 bg-white rounded-lg shadow-xl max-w-4xl p-6" style={{ maxHeight: "calc(100vh - 4rem)", overflowY: "auto" }}>
        <h2 className="text-xl font-semibold text-gray-900">
          {definition ? "Edit mail-order pharmacy" : "Add mail-order pharmacy"}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          DoseSpot queries discover the current pharmacy ID. Matching rules prevent a similarly named local pharmacy from being trusted.
        </p>

        <div className="mt-5 space-y-5">
          {apiError && <ApiError error={apiError} setApiError={setApiError} />}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField id="mail-order-provider" label="Provider key" isRequired isDisabled={!!definition} value={form.provider} onChange={(value) => setField("provider", value)} />
            <InputField id="mail-order-display-name" label="Display name" isRequired value={form.displayName} onChange={(value) => setField("displayName", value)} />
            <InputField id="mail-order-portal-url" label="Portal URL" value={form.portalUrl} onChange={(value) => setField("portalUrl", value)} />
            <InputField id="mail-order-sort-order" label="Sort order" type="number" value={String(form.sortOrder)} onChange={(value) => setField("sortOrder", value)} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">DoseSpot queries</h3>
                <p className="text-xs text-gray-500">Queries run in order and matching results are deduplicated by pharmacy ID.</p>
              </div>
              <Button btnText="Add query" variant="Secondary" className="h-9 px-2" onClick={() => setQueryRows((rows) => [...rows, { field: "name", value: "" }])} />
            </div>
            <div className="space-y-2">
              {queryRows.map((query, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-center">
                  <select
                    aria-label={`Query ${index + 1} field`}
                    className="col-span-4 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                    value={query.field}
                    onChange={(event) => setQueryRows((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, field: event.target.value } : row))}
                  >
                    {QUERY_FIELDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  <input
                    aria-label={`Query ${index + 1} value`}
                    className="col-span-7 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    value={query.value}
                    onChange={(event) => {
                      setQueryRows((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, value: event.target.value } : row));
                      setPreviewResult(null);
                    }}
                  />
                  <button
                    type="button"
                    className="col-span-1 text-sm text-red-600 disabled:text-gray-300"
                    disabled={queryRows.length === 1}
                    onClick={() => setQueryRows((rows) => rows.filter((_, rowIndex) => rowIndex !== index))}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900">Identity matching</h3>
            <p className="text-xs text-gray-500 mb-3">A result must match an NCPDP ID or normalized name, plus every optional location field entered below.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField id="mail-order-name-matchers" label="Name matchers (comma separated)" value={form.nameMatchers} onChange={(value) => setField("nameMatchers", value)} />
              <InputField id="mail-order-ncpdp-ids" label="NCPDP IDs (comma separated)" value={form.ncpdpIds} onChange={(value) => setField("ncpdpIds", value)} />
              <InputField id="mail-order-match-address" label="Exact address (optional)" value={form.address} onChange={(value) => setField("address", value)} />
              <InputField id="mail-order-match-city" label="Exact city (optional)" value={form.city} onChange={(value) => setField("city", value)} />
              <InputField id="mail-order-match-state" label="Exact state (optional)" maxLength={2} value={form.state} onChange={(value) => setField("state", value)} />
              <InputField id="mail-order-match-zip" label="Exact ZIP (optional)" value={form.zip} onChange={(value) => setField("zip", value)} />
              <InputField id="mail-order-match-phone" label="Exact phone (optional)" value={form.phone} onChange={(value) => setField("phone", value)} />
            </div>
          </div>

          {previewResult && (
            <div className={`rounded-md p-4 ${previewResult.available ? "bg-green-50 text-green-800" : "bg-yellow-50 text-yellow-800"}`}>
              <div className="font-medium">Resolution: {previewResult.resolutionStatus}</div>
              {previewResult.pharmacy ? (
                <div className="mt-1 text-sm">
                  {previewResult.pharmacy.StoreName} · DoseSpot ID {previewResult.pharmacy.PharmacyId} · NCPDP {previewResult.pharmacy.NCPDPID || "—"}
                </div>
              ) : (
                <div className="mt-1 text-sm">No single safe pharmacy match was found. This definition cannot be activated.</div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button btnText="Cancel" variant="Secondary" className="h-10 px-4" onClick={onClose} />
          <Button btnText="Test DoseSpot" variant="Secondary" className="h-10 px-4" isDisabled={!canSubmit || preview.isLoading} isLoading={preview.isLoading} onClick={testResolution} />
          <Button btnText={definition ? "Save changes" : "Create inactive"} className="h-10 px-4" isDisabled={!canSubmit || saving} isLoading={saving} onClick={save} />
        </div>
      </div>
    </div>
  );
};
